import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { fmtDateDMY } from "../utils";
import type { QuickReportData } from "../types";

type EmailFormat = "excel" | "csv" | "pdf";

interface Props {
  open: boolean;
  onClose: () => void;
  data: QuickReportData;
  businessName: string;
  userName?: string;
}

const EXT: Record<EmailFormat, string> = { excel: ".xlsx", csv: ".csv", pdf: ".pdf" };

export default function SendReportModal({ open, onClose, data, businessName, userName }: Props) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("Account quick report");
  const [format, setFormat] = useState<EmailFormat>("excel");
  const [message, setMessage] = useState(
    `Hello,\n\nAttached is the 'Account quick report' report for ${businessName}.\n\nRegards,\n${userName ?? ""}`
  );
  const [filename, setFilename] = useState("Account quick report");

  if (!open) return null;

  const downloadFile = () => {
    const headers = ["Distribution account", "Date", "Type", "Ref no", "Payee", "Description", "Account", "Cleared", "Amount", "Balance"];
    const rows = data.groups.flatMap((g) =>
      g.rows.map((r) => [r.distributionAccount, fmtDateDMY(r.date), r.type, r.refNo, r.payee, r.description, r.accountName, r.cleared, r.amount.toFixed(2), r.balance.toFixed(2)])
    );

    if (format === "pdf") {
      window.print();
      return;
    }
    const sep = format === "csv" ? "," : "\t";
    const text = [headers, ...rows].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(sep)).join("\n");
    const mime = format === "csv" ? "text/csv" : "application/vnd.ms-excel";
    const blob = new Blob([text], { type: `${mime};charset=utf-8;` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}${EXT[format]}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSend = () => {
    downloadFile();
    const mailto = `mailto:${encodeURIComponent(to)}?cc=${encodeURIComponent(cc)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
    window.location.href = mailto;
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EEF1F4]">
          <h2 className="text-base font-bold text-ink">Send account quick report</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-ink3 hover:text-ink">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4">
          <Field label="To"><input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Separate multiple emails with commas" className="w-full h-9 border border-[#D8DEE6] rounded-md px-3 text-[12.5px] bg-white" /></Field>
          <Field label="Cc"><input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Separate multiple emails with commas" className="w-full h-9 border border-[#D8DEE6] rounded-md px-3 text-[12.5px] bg-white" /></Field>
          <Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full h-9 border border-[#D8DEE6] rounded-md px-3 text-[12.5px] bg-white" /></Field>
          <Field label="Format">
            <div className="flex gap-5">
              {(["excel", "csv", "pdf"] as EmailFormat[]).map((f) => (
                <label key={f} className="inline-flex items-center gap-1.5 text-[12.5px] cursor-pointer">
                  <input type="radio" name="fmt" checked={format === f} onChange={() => setFormat(f)} />
                  {f === "excel" ? "Excel" : f === "csv" ? "CSV" : "PDF"}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Message" align="start">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} className="w-full min-h-[110px] border border-[#D8DEE6] rounded-md px-3 py-2 text-[12.5px] font-sans resize-y bg-white" />
          </Field>
          <Field label="File name">
            <div className="flex items-center gap-2">
              <input value={filename} onChange={(e) => setFilename(e.target.value)} className="flex-1 h-9 border border-[#D8DEE6] rounded-md px-3 text-[12.5px] bg-white" />
              <span className="text-[11.5px] text-ink3">{EXT[format]}</span>
            </div>
          </Field>
        </div>
        <div className="flex justify-end gap-2.5 px-5 py-3 border-t border-[#EEF1F4]">
          <button type="button" onClick={onClose} className="h-9 px-4 border border-[#D8DEE6] rounded-[10px] text-[12.5px] font-semibold text-ink bg-white">Cancel</button>
          <button type="button" onClick={handleSend} className="h-9 px-4 border border-ink rounded-[10px] text-[12.5px] font-semibold text-white bg-ink">Send email</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, children, align }: { label: string; children: React.ReactNode; align?: "start" }) {
  return (
    <div className={`grid gap-3 mb-3 ${align === "start" ? "items-start" : "items-center"}`} style={{ gridTemplateColumns: "90px 1fr" }}>
      <label className="text-[11.5px] text-ink2 font-semibold">{label}</label>
      {children}
    </div>
  );
}